import { a as __rest } from '../../_tslib.esm.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import React__default, { forwardRef } from 'react';
import { Squares2X2Icon } from '@iris/react-icons';
import * as RadixToggle from '@radix-ui/react-toggle-group';
import toggleButtonStyles from './ToggleButton.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

const ToggleButton = forwardRef((_a, ref) => {
  var _b;
  var {
      choiceCount,
      onClick
    } = _a,
    props = __rest(_a, ["choiceCount", "onClick"]);
  // mapping props
  const styleProps = {
    variant: props.variant,
    inverse: props.inverse
  };
  const rootProps = {
    defaultValue: (_b = props.defaultValue) !== null && _b !== void 0 ? _b : choiceCount[0].value,
    orientation: props.orientation,
    className: props.className,
    "aria-label": props["aria-label"],
    "aria-labelledby": props["aria-labelledby"],
    onValueChange: props.onValueChange
  };
  // get the styles from tw-variants
  const {
    container: containerClass,
    base: baseClass,
    baseSelected: baseSelectedClass,
    baseLeft: baseLeftClass,
    baseRight: baseRightClass,
    icon: iconClass,
    iconSelected: iconSelectedClass,
    contentContainer: contentContainerClass,
    label: labelClass,
    labelSelected: labelSelectedClass
  } = toggleButtonStyles(styleProps);
  const generateItemElement = (icon, isSelected, label) => {
    const styledIcon = React__default.cloneElement(icon, {
      className: cn(icon === null || icon === void 0 ? void 0 : icon.props.className, iconClass(), isSelected && iconSelectedClass())
    });
    switch (props.variant) {
      case "icon":
        return jsx("div", {
          className: contentContainerClass(),
          children: styledIcon
        });
      case "label":
        return jsx("div", {
          className: contentContainerClass(),
          children: jsx("span", {
            className: cn(labelClass(), isSelected && labelSelectedClass()),
            children: label
          })
        });
      case "both":
        return jsxs("div", {
          className: contentContainerClass(),
          children: [styledIcon, jsx("span", {
            className: cn(labelClass(), isSelected && labelSelectedClass()),
            children: label
          })]
        });
    }
  };
  return jsx(RadixToggle.Root, Object.assign({
    ref: ref,
    type: "single",
    defaultValue: rootProps.defaultValue,
    value: props.value,
    orientation: rootProps.orientation,
    rovingFocus: props.rovingFocus,
    loop: props.loop,
    className: cn(containerClass(), rootProps.className),
    "aria-label": rootProps["aria-label"] || "Toggle button"
  }, rootProps["aria-labelledby"] ? {
    "aria-labelledby": rootProps["aria-labelledby"]
  } : {}, {
    onValueChange: value => {
      var _a;
      if (!value) return;
      (_a = rootProps === null || rootProps === void 0 ? void 0 : rootProps.onValueChange) === null || _a === void 0 ? void 0 : _a.call(rootProps, value);
    },
    onClick: onClick,
    children: choiceCount === null || choiceCount === void 0 ? void 0 : choiceCount.map((choice, idx) => {
      return jsx(RadixToggle.Item, {
        value: choice.value,
        tabIndex: 0,
        role: "button",
        "aria-pressed": props.value === choice.value ? true : false,
        "aria-label": props.variant === "icon" ? `${idx + 1} of ${choiceCount.length}` : `${choice.label} ${idx + 1} of ${choiceCount.length} `,
        className: cn({
          [baseSelectedClass()]: choice.value === props.value
        }, {
          [baseLeftClass()]: idx === 0
        }, {
          [baseRightClass()]: idx === choiceCount.length - 1
        }, baseClass()),
        children: generateItemElement(choice.icon || jsx(Squares2X2Icon, {}), choice.value === props.value, choice.label)
      }, `${choice.value}-${idx}`);
    })
  }));
});
ToggleButton.displayName = "ToggleButton";

export { ToggleButton };
